/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { login } from '../core/_requests'
import { useAuth } from '../core/Auth'

export function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [googleAuthenticatorToken, setGoogleAuthenticatorToken] = useState('')
  const [loading, setLoading] = useState(false)
  // global error message state removed; using react-toastify to show toasts
  const [showPassword, setShowPassword] = useState(false)

  // Validation state
  const [errors, setErrors] = useState<{ email?: string; password?: string; google_authenticator_token?: string }>({})
  const [touched, setTouched] = useState<{ email?: boolean; password?: boolean; google_authenticator_token?: boolean }>({})

  const { saveAuth, saveUser, logout } = useAuth()
  const navigate = useNavigate()

  const validateEmail = (value: string) => {
    if (!value) return 'Email is required'
    // simple email regex
    const re = /^\S+@\S+\.\S+$/
    if (!re.test(value)) return 'Enter a valid email address'
    return ''
  }

  const validatePassword = (value: string) => {
    if (!value) return 'Password is required'
    return ''
  }

  const validateGoogleAuthenticatorToken = (value: string) => {
    if (!value) return 'Google authenticator token is required'
    if (!/^\d+$/.test(value)) return 'Google authenticator token must contain numbers only'
    if (value.length !== 6) return 'Google authenticator token must be 6 digits'
    return ''
  }

  const validateField = (name: string, value: string) => {
    if (name === 'email') return validateEmail(value)
    if (name === 'password') return validatePassword(value)
    if (name === 'google_authenticator_token') return validateGoogleAuthenticatorToken(value)
    return ''
  }

  const validateAll = () => {
    const e: { email?: string; password?: string; google_authenticator_token?: string } = {}
    const emailErr = validateEmail(email)
    if (emailErr) e.email = emailErr
    const pwErr = validatePassword(password)
    if (pwErr) e.password = pwErr
    const googleAuthenticatorTokenErr = validateGoogleAuthenticatorToken(googleAuthenticatorToken)
    if (googleAuthenticatorTokenErr) e.google_authenticator_token = googleAuthenticatorTokenErr
    return e
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setTouched((t) => ({ ...t, [name]: true }))
    const err = validateField(name, value)
    setErrors((prev) => ({ ...prev, [name]: err || undefined }))
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target
    const value = name === 'google_authenticator_token' ? e.target.value.replace(/\D/g, '').slice(0, 6) : e.target.value

    if (name === 'email') setEmail(value)
    if (name === 'password') setPassword(value)
    if (name === 'google_authenticator_token') setGoogleAuthenticatorToken(value)

    // If field was touched or has an error, re-validate on change
    if (
      touched[name as 'email' | 'password' | 'google_authenticator_token'] ||
      errors[name as 'email' | 'password' | 'google_authenticator_token']
    ) {
      const err = validateField(name, value)
      setErrors((prev) => ({ ...prev, [name]: err || undefined }))
    }

    // toasts are ephemeral; nothing to clear when user edits
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault()
    setLoading(true)

    // Mark all fields touched and validate
    setTouched({ email: true, password: true, google_authenticator_token: true })
    const eAll = validateAll()
    setErrors(eAll)

    if (Object.keys(eAll).length > 0) {
      const messages = Object.values(eAll).filter(Boolean).join('. ');
      toast.error(messages || 'Please fix validation errors');
      setLoading(false);
      return
    }

    try {
      const resp = await login(email, password, googleAuthenticatorToken)
      saveAuth({ api_token: resp.token })
      saveUser(resp.user);
      toast.success(resp.message || 'Login successful');

      navigate('/')
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Invalid login details';
      toast.error(errorMessage);
      logout();
    } finally {
      setLoading(false);
    }
  }

  const isFormValid =
    !validateEmail(email) && !validatePassword(password) && !validateGoogleAuthenticatorToken(googleAuthenticatorToken)

  return (
    <form className='form w-100' onSubmit={handleSubmit} noValidate>
      <div className='text-center mb-11'>
        <h1 className='text-black fw-bolder mb-4'>Welcome Back!</h1>
        <div className='text-dark font-14'>Log in with your credentials to access your dashboard</div>
      </div>

      {/* Global error messages are displayed via react-toastify toasts */}

      {/* Email / Phone */}
      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>
          Email <span className='text-danger'>*</span>
        </label>

        <input
          name='email'
          type='text'
          className={`form-control bg-transparent ${touched.email ? (errors.email ? 'is-invalid' : 'is-valid') : ''}`}
          placeholder='Enter your email'
          value={email}
          onChange={handleChange}
          onBlur={handleBlur}
          autoComplete='off'
        />
        {errors.email && <div className='invalid-feedback'>{errors.email}</div>}
      </div>

      {/* Password */}
      <div className='fv-row mb-10 password-field'>
        <label className='form-label fs-6 fw-bolder text-dark'>
          Password <span className='text-danger'>*</span>
        </label>

        <div className='input-group'>
          <input
            name='password'
            type={showPassword ? 'text' : 'password'}
            className={`form-control bg-transparent ${touched.password ? (errors.password ? 'is-invalid' : 'is-valid') : ''}`}
            placeholder='Enter password'
            value={password}
            onChange={handleChange}
            onBlur={handleBlur}
            autoComplete='off'
          />
          <button
            type='button'
            className='btn btn-outline-secondary'
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <i className={`bi bi-${showPassword ? 'eye' : 'eye-slash'}`}></i>
          </button>
        </div>
        {errors.password && <div className='invalid-feedback d-block'>{errors.password}</div>}
      </div>

      {/* Google Authenticator Token */}
      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>
          Google Authenticator Token <span className='text-danger'>*</span>
        </label>

        <input
          name='google_authenticator_token'
          type='text'
          inputMode='numeric'
          maxLength={6}
          pattern='[0-9]{6}'
          className={`form-control bg-transparent ${
            touched.google_authenticator_token
              ? errors.google_authenticator_token
                ? 'is-invalid'
                : 'is-valid'
              : ''
          }`}
          placeholder='Enter authenticator token'
          value={googleAuthenticatorToken}
          onChange={handleChange}
          onBlur={handleBlur}
          autoComplete='one-time-code'
        />
        {errors.google_authenticator_token && (
          <div className='invalid-feedback'>{errors.google_authenticator_token}</div>
        )}
      </div>

      <div className='d-grid'>
        <button type='submit' className='btn btn-primary' disabled={loading || !isFormValid}>
          {!loading && <span>Sign In</span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              Please wait...
              <span className='spinner-border spinner-border-sm ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </form>
  )
}
